import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { RoleForm } from "../RoleForm/RoleForm";
import { useRoleMutations, usePrivilegeDefinitions } from "../api";
import { emptyRoleForm } from "../types";
import type { RoleFormValues } from "../types";

const FORM_ID = "role-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useRoleMutations();
  const { grouped, userPreset, isLoading } = usePrivilegeDefinitions();
  const [submitting, setSubmitting] = useState(false);
  // Remounting RoleForm via key resets it after "Save and Add Another".
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  // Native Save submits directly (nextAction stays "edit"); "Save and Add
  // Another" sets "new" then programmatically submits.
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: RoleFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create role", err);
      setToast(saveErrorToast(err, "role"));
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminFormPage
        title="Add a Role"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <RoleForm
          key={formKey}
          id={FORM_ID}
          initialValues={{ ...emptyRoleForm, privileges: userPreset }}
          definitions={grouped}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
